import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationMeta, type Notification } from '../../hooks/useNotifications';
import { useNotificationsContext } from '../../contexts/NotificationsContext';

type FilterKey = 'todas' | 'nao_lidas' | 'comunicados' | 'pagamentos' | 'pendencias' | 'sistema';

const FILTERS: { key: FilterKey; label: string; icon: string }[] = [
  { key: 'todas',       label: 'Todas',        icon: 'ri-list-check-3' },
  { key: 'nao_lidas',   label: 'Não lidas',    icon: 'ri-circle-fill' },
  { key: 'comunicados', label: 'Comunicados',  icon: 'ri-megaphone-line' },
  { key: 'pagamentos',  label: 'Pagamentos',   icon: 'ri-secure-payment-line' },
  { key: 'pendencias',  label: 'Pendências',   icon: 'ri-calendar-todo-line' },
  { key: 'sistema',     label: 'Sistema',      icon: 'ri-information-line' },
];

const matchesFilter = (n: Notification, filter: FilterKey): boolean => {
  if (filter === 'todas') return true;
  if (filter === 'nao_lidas') return !n.read_at;
  if (filter === 'comunicados') return n.type === 'broadcast';
  if (filter === 'pagamentos') return ['payment_approved', 'payment_rejected', 'payment_pending', 'upgrade_completed'].includes(n.type);
  if (filter === 'pendencias') return ['calendar_pending', 'calendar_overdue', 'card_due_reminder', 'card_expiring', 'account_expired', 'plan_limit_reached'].includes(n.type);
  if (filter === 'sistema') return ['info', 'warning', 'success', 'goal_completed'].includes(n.type);
  return true;
};

const fmtRelative = (dateStr: string): string => {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `${mins}min atrás`;
  if (hours < 24) return `${hours}h atrás`;
  if (days === 1) return 'ontem';
  if (days < 7) return `${days} dias atrás`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtAbsolute = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

const fmtDate = (dateStr: string): string => {
  const relative = fmtRelative(dateStr);
  const absolute = fmtAbsolute(dateStr);
  // If the relative already IS the absolute (>= 7 days), no need to duplicate
  if (relative === absolute) return absolute;
  return `${relative} | ${absolute}`;
};

const fmtDatetime = (dateStr: string): string =>
  new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const TYPE_LABEL: Record<string, string> = {
  broadcast: 'Comunicado',
  payment_approved: 'Pagamento',
  payment_rejected: 'Pagamento',
  payment_pending: 'Pagamento',
  upgrade_completed: 'Plano',
  plan_limit_reached: 'Limite',
  goal_completed: 'Meta',
  calendar_pending: 'Pendência',
  calendar_overdue: 'Vencido',
  card_due_reminder: 'Cartão',
  card_expiring: 'Cartão',
  account_expired: 'Conta',
  info: 'Sistema',
  warning: 'Alerta',
  success: 'Sucesso',
};

const TYPE_BADGE_COLOR: Record<string, string> = {
  broadcast: 'bg-violet-50 text-violet-600',
  payment_approved: 'bg-emerald-50 text-emerald-600',
  payment_rejected: 'bg-red-50 text-red-600',
  payment_pending: 'bg-yellow-50 text-yellow-600',
  upgrade_completed: 'bg-amber-50 text-amber-600',
  plan_limit_reached: 'bg-orange-50 text-orange-600',
  goal_completed: 'bg-emerald-50 text-emerald-600',
  calendar_pending: 'bg-amber-50 text-amber-600',
  calendar_overdue: 'bg-red-50 text-red-600',
  card_due_reminder: 'bg-red-50 text-red-600',
  card_expiring: 'bg-orange-50 text-orange-600',
  info: 'bg-sky-50 text-sky-600',
  warning: 'bg-amber-50 text-amber-600',
  success: 'bg-emerald-50 text-emerald-600',
};

// ── Notification Detail Modal ─────────────────────────────────────────────────
interface NotifModalProps {
  notification: Notification;
  onClose: () => void;
  onNavigate: (n: Notification) => void;
}

const NAVIGABLE_MODAL_TYPES = [
  'payment_rejected', 'payment_pending', 'upgrade_completed',
  'plan_limit_reached', 'goal_completed', 'calendar_pending', 'calendar_overdue',
];

const NAV_LABELS: Record<string, string> = {
  payment_rejected: 'Ver meu plano →',
  payment_pending: 'Ver meu plano →',
  upgrade_completed: 'Ver meu plano →',
  plan_limit_reached: 'Fazer upgrade →',
  goal_completed: 'Ver minhas metas →',
  calendar_pending: 'Ver no calendário →',
  calendar_overdue: 'Ver no calendário →',
};

const NotifDetailModal = ({ notification: n, onClose, onNavigate }: NotifModalProps) => {
  const meta = getNotificationMeta(n.type);
  const isNavigable = NAVIGABLE_MODAL_TYPES.includes(n.type);
  const navLabel = NAV_LABELS[n.type];
  const typeLabel = TYPE_LABEL[n.type] ?? 'Notificação';
  const badgeColor = TYPE_BADGE_COLOR[n.type] ?? 'bg-gray-50 text-gray-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with color accent */}
        <div className={`px-6 pt-6 pb-5 ${meta.bg} flex items-start gap-4`}>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/70`}>
            <i className={`${meta.icon} text-xl ${meta.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[11px] font-700 px-2 py-0.5 rounded-full ${badgeColor}`}>
                {typeLabel}
              </span>
              {!n.read_at && (
                <span className="w-2 h-2 bg-emerald-500 rounded-full" />
              )}
            </div>
            <h2 className="text-[15px] font-700 text-gray-900 leading-snug">{n.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-white/60 text-gray-500 cursor-pointer transition-colors flex-shrink-0"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Full body / message content */}
          {(() => {
            // For broadcasts, the rich content may be stored in data.body or data.content
            const broadcastContent = n.type === 'broadcast'
              ? ((n.data as Record<string, unknown>)?.body as string | undefined)
                ?? ((n.data as Record<string, unknown>)?.content as string | undefined)
              : undefined;

            if (broadcastContent) {
              return (
                <div
                  className="text-[14px] text-gray-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: broadcastContent }}
                />
              );
            }

            if (n.body) {
              return <p className="text-[14px] text-gray-700 leading-relaxed">{n.body}</p>;
            }

            return <p className="text-[14px] text-gray-400 italic">Sem detalhes adicionais.</p>;
          })()}

          {/* Date: relative | absolute — and full datetime below */}
          <div className="mt-5 pt-4 border-t border-gray-100 space-y-1">
            <div className="flex items-center gap-1.5 text-[12px] text-gray-400">
              <i className="ri-time-line text-[13px]" />
              <span>{fmtRelative(n.created_at)} &bull; {fmtDatetime(n.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-[13px] font-600 text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-colors"
          >
            Fechar
          </button>
          {isNavigable && navLabel && (
            <button
              onClick={() => onNavigate(n)}
              className="px-4 py-2 rounded-xl text-[13px] font-700 text-white bg-emerald-500 hover:bg-emerald-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              {navLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Notification Card ─────────────────────────────────────────────────────────
interface NotifCardProps {
  n: Notification;
  onRead: (id: string) => void;
  onOpenDetail: (n: Notification) => void;
}

const NotifCard = ({ n, onRead, onOpenDetail }: NotifCardProps) => {
  const meta = getNotificationMeta(n.type);
  const isUnread = !n.read_at;
  const typeLabel = TYPE_LABEL[n.type] ?? 'Notificação';
  const badgeColor = TYPE_BADGE_COLOR[n.type] ?? 'bg-gray-50 text-gray-600';

  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-5 transition-all ${
        isUnread
          ? 'bg-emerald-50/40 border-emerald-100'
          : 'bg-white border-gray-100 hover:border-gray-200'
      }`}
    >
      {/* Unread dot */}
      {isUnread && (
        <span className="absolute top-4 right-4 w-2.5 h-2.5 bg-emerald-500 rounded-full" />
      )}

      {/* Top: icon + badge */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
          <i className={`${meta.icon} text-base ${meta.color}`} />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <span className={`text-[10px] font-700 px-2 py-0.5 rounded-full ${badgeColor}`}>
            {typeLabel}
          </span>
        </div>
      </div>

      {/* Title + body */}
      <p className={`text-[13px] leading-snug text-gray-900 mb-1 ${isUnread ? 'font-700' : 'font-600'}`}>
        {n.title}
      </p>
      {n.body && (
        <p className="text-[12px] text-gray-500 leading-relaxed line-clamp-3 flex-1">
          {n.body}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100/80 gap-2">
        <span className="text-[11px] text-gray-400 whitespace-nowrap">{fmtDate(n.created_at)}</span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isUnread && (
            <button
              onClick={() => onRead(n.id)}
              className="text-[11px] text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              Lida
            </button>
          )}
          <button
            onClick={() => onOpenDetail(n)}
            className="text-[11px] font-700 text-emerald-600 hover:text-emerald-700 cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1"
          >
            Ver notificação
            <i className="ri-arrow-right-s-line text-[12px]" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
export default function NotificacoesPage() {
  const navigate = useNavigate();
  const { notifications, loading, unreadCount, markAllRead, markRead } = useNotificationsContext();
  const [filter, setFilter] = useState<FilterKey>('todas');
  const [detailNotif, setDetailNotif] = useState<Notification | null>(null);

  const filtered = useMemo(
    () => notifications.filter((n) => matchesFilter(n, filter)),
    [notifications, filter]
  );

  const handleOpenDetail = (n: Notification) => {
    markRead(n.id);
    setDetailNotif(n);
  };

  const handleNavigateFromModal = (n: Notification) => {
    setDetailNotif(null);
    if (['payment_rejected', 'upgrade_completed', 'payment_pending', 'plan_limit_reached'].includes(n.type)) {
      navigate('/perfil?tab=plano');
    } else if (n.type === 'goal_completed') {
      navigate('/metas');
    } else if (n.type === 'calendar_pending' || n.type === 'calendar_overdue') {
      const date = n.data?.navigate_date as string | undefined;
      const sourceId = n.data?.source_event_id as string | undefined;
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (sourceId) params.set('open_event_id', sourceId);
      navigate(`/calendario?${params.toString()}`);
    }
  };

  const filterUnreadCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      todas: notifications.filter(n => !n.read_at).length,
      nao_lidas: notifications.filter(n => !n.read_at).length,
      comunicados: notifications.filter(n => !n.read_at && n.type === 'broadcast').length,
      pagamentos: notifications.filter(n => !n.read_at && ['payment_approved','payment_rejected','payment_pending','upgrade_completed'].includes(n.type)).length,
      pendencias: notifications.filter(n => !n.read_at && ['calendar_pending','calendar_overdue','card_due_reminder','card_expiring','account_expired','plan_limit_reached'].includes(n.type)).length,
      sistema: notifications.filter(n => !n.read_at && ['info','warning','success','goal_completed'].includes(n.type)).length,
    };
    return counts;
  }, [notifications]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Notificações</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {unreadCount > 0
              ? `${unreadCount} não lida${unreadCount > 1 ? 's' : ''}`
              : 'Tudo em dia por aqui'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-check-double-line text-base" />
              Marcar todas como lidas
            </button>
          )}
          <button
            onClick={() => navigate('/perfil?tab=notificacoes')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-500 text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-settings-3-line text-base" />
            Preferências
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 mb-5">
        <div className="flex gap-1 p-2 overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-500 whitespace-nowrap cursor-pointer transition-all flex-shrink-0 ${
                filter === f.key
                  ? 'bg-emerald-50 text-emerald-700 font-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className={`${f.icon} text-sm`} />
              {f.label}
              {filterUnreadCounts[f.key] > 0 && f.key !== 'nao_lidas' && (
                <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-red-100 text-red-600 text-[10px] font-700 rounded-full px-1">
                  {filterUnreadCounts[f.key]}
                </span>
              )}
              {f.key === 'nao_lidas' && filterUnreadCounts.nao_lidas > 0 && (
                <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-700 rounded-full px-1">
                  {filterUnreadCounts.nao_lidas}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Card grid */}
      {loading ? (
        <div className="py-20 text-center">
          <i className="ri-loader-4-line animate-spin text-gray-400 text-2xl" />
          <p className="text-sm text-gray-500 mt-3">Carregando notificações...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-notification-off-line text-gray-400 text-2xl" />
          </div>
          <p className="text-[15px] font-600 text-gray-700">
            {filter === 'nao_lidas' ? 'Tudo lido!' : 'Nenhuma notificação aqui'}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {filter === 'nao_lidas'
              ? 'Você está em dia com todas as notificações.'
              : 'Ainda não há notificações nesta categoria.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((n) => (
            <NotifCard
              key={n.id}
              n={n}
              onRead={markRead}
              onOpenDetail={handleOpenDetail}
            />
          ))}
        </div>
      )}

      {notifications.length > 0 && (
        <p className="text-center text-xs text-gray-400 mt-5">
          Exibindo as últimas {notifications.length} notificações
        </p>
      )}

      {/* Notification detail modal */}
      {detailNotif && (
        <NotifDetailModal
          notification={detailNotif}
          onClose={() => setDetailNotif(null)}
          onNavigate={handleNavigateFromModal}
        />
      )}
    </div>
  );
}
